import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../services/api';
import EditCandidateModal from './EditCandidateModal';
import EditConstituencyModal from './EditConstituencyModal';
import { getFullImageUrl } from '../../config/environment';
import './ViewData.css';

const ViewData = () => {
  const { t } = useTranslation();
  const [dataType, setDataType] = useState('candidates');
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [error, setError] = useState(null);
  const [message, setMessage] = useState(null);

  const [selectedItem, setSelectedItem] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(20);

  // ✅ NEW: Track constituency filter
  const [constituencyFilter, setConstituencyFilter] = useState(null);

  useEffect(() => {
    loadData();
  }, [dataType]);

  const loadData = async () => {
    setLoading(true);
    setError(null);
    setMessage(null);
    try {
      let result;
      if (dataType === 'constituencies') {
        result = await api.getConstituencies();
      } else if (dataType === 'candidates') {
        result = await api.getCandidates({ pageSize: 10000 });
        result = result.candidates || result;
      }

      if (Array.isArray(result)) {
        setData(result);
      } else if (result && Array.isArray(result.data)) {
        setData(result.data);
      } else if (result && typeof result === 'object') {
        setData(Object.values(result));
      } else {
        setData([]);
      }
    } catch (error) {
      console.error('Error loading data:', error);
      setError(error.message || t('viewData.loadError') || 'Failed to load data');
      setData([]);
    } finally {
      setLoading(false);
    }
  };

  // ✅ NEW: Handle constituency click - switch to candidates tab and filter
  const handleConstituencyClick = (constituency) => {
    console.log('🔍 Filtering candidates for constituency:', constituency.code, constituency.name);
    
    // Switch to candidates tab
    setDataType('candidates');
    
    // Set search term to constituency code
    setSearchTerm(constituency.code);
    
    // Store constituency filter for banner display
    setConstituencyFilter(constituency);
    
    // Reset to first page
    setCurrentPage(1);
  };

  // ✅ NEW: Clear constituency filter
  const clearConstituencyFilter = () => {
    setConstituencyFilter(null);
    setSearchTerm('');
    setCurrentPage(1);
  };

  const handleEdit = (item) => {
    setSelectedItem(item);
    setShowEditModal(true);
  };

  const handleDelete = (item) => {
    setDeleteConfirm(item);
  };

  const confirmDelete = async () => {
    if (!deleteConfirm) return;

    try {
      if (dataType === 'constituencies') {
        await api.deleteConstituency(deleteConfirm.id);
        setMessage({
          type: 'success',
          text: `Constituency "${deleteConfirm.name}" deleted successfully`
        });
      } else {
        await api.deleteCandidate(deleteConfirm.id);
        setMessage({
          type: 'success',
          text: `Candidate "${deleteConfirm.name || deleteConfirm.candidateName}" deleted successfully`
        });
      }
      setDeleteConfirm(null);
      loadData();
    } catch (error) {
      console.error('Error deleting:', error);
      setMessage({
        type: 'error',
        text: error.message || 'Failed to delete'
      });
    }
  };

  const handleUpdateSuccess = () => {
    setShowEditModal(false);
    setSelectedItem(null);
    setMessage({
      type: 'success',
      text: `${dataType === 'constituencies' ? 'Constituency' : 'Candidate'} updated successfully`
    });
    loadData();
  };

  const filteredData = Array.isArray(data)
    ? data.filter(item => {
        if (!item) return false;
        const searchLower = searchTerm.toLowerCase();

        if (dataType === 'constituencies') {
          return (
            item.name?.toLowerCase().includes(searchLower) ||
            item.code?.toLowerCase().includes(searchLower) ||
            item.district?.toLowerCase().includes(searchLower) ||
            String(item.phase || '').toLowerCase().includes(searchLower)
          );
        } else {
          return (
            item.name?.toLowerCase().includes(searchLower) ||
            item.candidateName?.toLowerCase().includes(searchLower) ||
            item.partyShortName?.toLowerCase().includes(searchLower) ||
            item.partyName?.toLowerCase().includes(searchLower) ||
            item.email?.toLowerCase().includes(searchLower) ||
            String(item.mobile || '').toLowerCase().includes(searchLower) ||
            item.constituencyCode?.toLowerCase().includes(searchLower) ||
            item.constituencyName?.toLowerCase().includes(searchLower) ||
            String(item.affidavitPdfUrl || '').toLowerCase().includes(searchLower)
          );
        }
      })
    : [];

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredData.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredData.length / itemsPerPage);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  const formatCurrency = (amount) => {
    if (!amount) return '₹0';
    return `₹${new Intl.NumberFormat('en-IN').format(amount)}`;
  };

  const getPhaseLabel = (phase) => (!phase ? 'Not Assigned' : `Phase ${phase}`);
  const getPhaseClass = (phase) => (!phase ? 'phase-none' : `phase-${phase}`);

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      setMessage({ type: 'success', text: 'Copied to clipboard' });
      setTimeout(() => setMessage(null), 1200);
    } catch {
      setMessage({ type: 'error', text: 'Copy failed' });
      setTimeout(() => setMessage(null), 1200);
    }
  };

  const getSymbolUrl = (item) => {
    return item.customSymbolUrl || item.symbolUrl || item.partySymbolUrl || null;
  };

  const getAffidavitUrl = (url) => {
    if (!url) return null;
    if (url.startsWith('http://') || url.startsWith('https://')) {
      return url;
    }
    return getFullImageUrl(url);
  };

  const openAffidavit = (url) => {
    const fullUrl = getAffidavitUrl(url);
    if (fullUrl) {
      window.open(fullUrl, '_blank');
    }
  };

  return (
    <div className="view-data-container">
      <div className="view-header">
        <h2>
          <i className="fas fa-table"></i>
          {t('viewData.title') || 'View Data'}
        </h2>
        <p>{t('viewData.subtitle') || 'View and manage all data'}</p>
      </div>

      {message && (
        <div className={`alert alert-${message.type}`}>
          <i className={`fas fa-${message.type === 'success' ? 'check-circle' : 'exclamation-circle'}`}></i>
          {message.text}
          <button onClick={() => setMessage(null)} className="close-alert">×</button>
        </div>
      )}

      <div className="data-controls">
        <div className="data-type-selector">
          <button
            className={`type-btn ${dataType === 'constituencies' ? 'active' : ''}`}
            onClick={() => {
              setDataType('constituencies');
              setCurrentPage(1);
              setConstituencyFilter(null);
              setSearchTerm('');
            }}
          >
            <i className="fas fa-map-marked-alt"></i>
            <span>{t('viewData.constituencies') || 'Constituencies'}</span>
          </button>
          <button
            className={`type-btn ${dataType === 'candidates' ? 'active' : ''}`}
            onClick={() => {
              setDataType('candidates');
              setCurrentPage(1);
            }}
          >
            <i className="fas fa-users"></i>
            <span>{t('viewData.candidates') || 'Candidates'}</span>
          </button>
        </div>

        <div className="search-box">
          <i className="fas fa-search"></i>
          <input
            type="text"
            placeholder={t('viewData.searchPlaceholder') || 'Search...'}
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setCurrentPage(1);
              // Clear constituency filter if user manually changes search
              if (constituencyFilter && e.target.value !== constituencyFilter.code) {
                setConstituencyFilter(null);
              }
            }}
          />
        </div>

        <button onClick={loadData} className="refresh-btn">
          <i className="fas fa-sync-alt"></i>
          <span>{t('viewData.refresh') || 'Refresh'}</span>
        </button>
      </div>

      {/* ✅ NEW: Constituency Filter Banner */}
      {constituencyFilter && dataType === 'candidates' && (
        <div className="filter-banner">
          <div className="filter-info">
            <i className="fas fa-filter"></i>
            <span>
              Showing candidates from <strong>{constituencyFilter.name}</strong> 
              <small>({constituencyFilter.code} - {constituencyFilter.district})</small>
            </span>
          </div>
          <button className="clear-filter-btn" onClick={clearConstituencyFilter}>
            <i className="fas fa-times"></i>
            Clear Filter
          </button>
        </div>
      )}

      {loading ? (
        <div className="loading-container">
          <div className="spinner-large"></div>
          <p>{t('viewData.loading') || 'Loading...'}</p>
        </div>
      ) : error ? (
        <div className="error-container">
          <i className="fas fa-exclamation-circle"></i>
          <p>{error}</p>
          <button onClick={loadData} className="retry-btn">
            <i className="fas fa-redo"></i>
            {t('viewData.tryAgain') || 'Try Again'}
          </button>
        </div>
      ) : (
        <div className="data-table-container">
          <p className="data-count">
            {t('viewData.showing') || 'Showing'} {currentItems.length} {t('viewData.of') || 'of'} {filteredData.length} {t('viewData.records') || 'records'}
          </p>

          {currentItems.length > 0 ? (
            <>
              {/* Desktop Table View */}
              <div className="desktop-table">
                {dataType === 'constituencies' ? (
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>{t('viewData.code') || 'Code'}</th>
                        <th>{t('viewData.name') || 'Name'}</th>
                        <th>{t('viewData.district') || 'District'}</th>
                        <th>Phase</th>
                        <th>{t('viewData.pollDate') || 'Poll Date'}</th>
                        <th>{t('viewData.resultDate') || 'Result Date'}</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {currentItems.map((item, idx) => (
                        <tr key={item.id || idx}>
                          <td><span className="constituency-code">{item.code || 'N/A'}</span></td>
                          <td>
                            {/* ✅ CLICKABLE CONSTITUENCY NAME */}
                            <strong
                              className="constituency-name-link"
                              onClick={() => handleConstituencyClick(item)}
                              title="Click to view candidates"
                            >
                              <i className="fas fa-users"></i>
                              {item.name || 'N/A'}
                            </strong>
                          </td>
                          <td>{item.district || 'N/A'}</td>
                          <td>
                            <span className={`phase-badge ${getPhaseClass(item.phase)}`}>
                              {getPhaseLabel(item.phase)}
                            </span>
                          </td>
                          <td>{item.pollDate ? new Date(item.pollDate).toLocaleDateString('en-IN') : 'N/A'}</td>
                          <td>{item.resultDate ? new Date(item.resultDate).toLocaleDateString('en-IN') : 'N/A'}</td>
                          <td>
                            <div className="action-buttons">
                              <button onClick={() => handleEdit(item)} className="btn-action btn-edit" title="Edit">
                                <i className="fas fa-edit"></i>
                              </button>
                              <button onClick={() => handleDelete(item)} className="btn-action btn-delete" title="Delete">
                                <i className="fas fa-trash"></i>
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Photo</th>
                        <th>{t('viewData.candidateName') || 'Name'}</th>
                        <th>{t('viewData.party') || 'Party'}</th>
                        <th>Symbol</th>
                        <th>{t('viewData.constituency') || 'Constituency'}</th>
                        <th>{t('viewData.age') || 'Age'}</th>
                        <th>Email</th>
                        <th>Mobile</th>
                        <th>Assets</th>
                        <th>Criminal Cases</th>
                        <th>Affidavit PDF</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {currentItems.map((item, idx) => (
                        <tr key={item.id || idx}>
                          <td>
                            <div className="candidate-photo">
                              {item.photoUrl ? (
                                <img
                                  src={getFullImageUrl(item.photoUrl)}
                                  alt={item.name || item.candidateName}
                                  onError={(e) => {
                                    e.target.style.display = 'none';
                                    e.target.parentElement.innerHTML = '<div class="photo-placeholder"><i class="fas fa-user"></i></div>';
                                  }}
                                />
                              ) : (
                                <div className="photo-placeholder"><i className="fas fa-user"></i></div>
                              )}
                            </div>
                          </td>
                          <td><strong>{item.name || item.candidateName || 'N/A'}</strong></td>
                          <td>
                            <div className="party-cell">
                              <span className="party-badge">{item.partyShortName || 'IND'}</span>
                              <small className="party-name">{item.partyName}</small>
                            </div>
                          </td>
                          <td>
                            <div className="party-symbol">
                              {getSymbolUrl(item) ? (
                                <img
                                  src={getFullImageUrl(getSymbolUrl(item))}
                                  alt={`${item.partyShortName} symbol`}
                                  className="symbol-img"
                                  onError={(e) => {
                                    e.target.style.display = 'none';
                                    e.target.parentElement.innerHTML = '<div class="no-symbol-icon"><i class="fas fa-flag"></i></div>';
                                  }}
                                />
                              ) : (
                                <div className="no-symbol-icon"><i className="fas fa-flag"></i></div>
                              )}
                            </div>
                          </td>
                          <td>
                            <div className="constituency-cell">
                              <span className="code">{item.constituencyCode}</span>
                              <small>{item.constituencyName || 'N/A'}</small>
                            </div>
                          </td>
                          <td>{item.age || 'N/A'}</td>
                          <td>
                            {item.email ? (
                              <div className="contact-cell">
                                <a href={`mailto:${item.email}`} className="link">{item.email}</a>
                                <button
                                  className="icon-btn"
                                  title="Copy email"
                                  onClick={() => copyToClipboard(item.email)}
                                >
                                  <i className="far fa-copy"></i>
                                </button>
                              </div>
                            ) : (
                              <span className="muted">—</span>
                            )}
                          </td>
                          <td>
                            {item.mobile ? (
                              <div className="contact-cell">
                                <a href={`tel:${item.mobile}`} className="link">{item.mobile}</a>
                                <button
                                  className="icon-btn"
                                  title="Copy mobile"
                                  onClick={() => copyToClipboard(item.mobile)}
                                >
                                  <i className="far fa-copy"></i>
                                </button>
                              </div>
                            ) : (
                              <span className="muted">—</span>
                            )}
                          </td>
                          <td>{formatCurrency(item.assets)}</td>
                          <td>
                            <span className={`criminal-badge ${item.criminalCases > 0 ? 'has-cases' : 'no-cases'}`}>
                              {item.criminalCases || 0}
                            </span>
                          </td>
                          <td>
                            {item.affidavitPdfUrl ? (
                              <div className="affidavit-cell">
                                <button
                                  className="btn-affidavit"
                                  onClick={() => openAffidavit(item.affidavitPdfUrl)}
                                  title="Download Affidavit PDF"
                                >
                                  <i className="fas fa-file-pdf"></i>
                                  <span>View PDF</span>
                                </button>
                              </div>
                            ) : (
                              <span className="muted">—</span>
                            )}
                          </td>
                          <td>
                            <div className="action-buttons">
                              <button onClick={() => handleEdit(item)} className="btn-action btn-edit" title="Edit">
                                <i className="fas fa-edit"></i>
                              </button>
                              <button onClick={() => handleDelete(item)} className="btn-action btn-delete" title="Delete">
                                <i className="fas fa-trash"></i>
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>

              {/* Mobile Cards View */}
              <div className="mobile-cards">
                {dataType === 'constituencies'
                  ? currentItems.map((item, idx) => (
                      <div key={item.id || idx} className="data-card">
                        <div className="card-header">
                          <span className="card-code">{item.code || 'N/A'}</span>
                          {/* ✅ CLICKABLE CONSTITUENCY NAME (Mobile) */}
                          <span
                            className="card-name constituency-name-link"
                            onClick={() => handleConstituencyClick(item)}
                          >
                            <i className="fas fa-users"></i>
                            {item.name || 'N/A'}
                          </span>
                        </div>
                        <div className="card-body">
                          <div className="card-row">
                            <span className="card-label"><i className="fas fa-map-marker-alt"></i> District:</span>
                            <span className="card-value">{item.district || 'N/A'}</span>
                          </div>
                          <div className="card-row">
                            <span className="card-label"><i className="fas fa-layer-group"></i> Phase:</span>
                            <span className="card-value">
                              <span className={`phase-badge ${getPhaseClass(item.phase)}`}>{getPhaseLabel(item.phase)}</span>
                            </span>
                          </div>
                          <div className="card-row">
                            <span className="card-label"><i className="far fa-calendar"></i> Poll Date:</span>
                            <span className="card-value">{item.pollDate ? new Date(item.pollDate).toLocaleDateString('en-IN') : 'N/A'}</span>
                          </div>
                          <div className="card-row">
                            <span className="card-label"><i className="far fa-calendar-check"></i> Result Date:</span>
                            <span className="card-value">{item.resultDate ? new Date(item.resultDate).toLocaleDateString('en-IN') : 'N/A'}</span>
                          </div>
                          <div className="card-actions">
                            <button onClick={() => handleEdit(item)} className="btn-action btn-edit">
                              <i className="fas fa-edit"></i> Edit
                            </button>
                            <button onClick={() => handleDelete(item)} className="btn-action btn-delete">
                              <i className="fas fa-trash"></i> Delete
                            </button>
                          </div>
                        </div>
                      </div>
                    ))
                  : currentItems.map((item, idx) => (
                      <div key={item.id || idx} className="data-card">
                        <div className="card-header">
                          <div className="card-photo">
                            {item.photoUrl ? (
                              <img
                                src={getFullImageUrl(item.photoUrl)}
                                alt={item.name || item.candidateName}
                                onError={(e) => {
                                  e.target.style.display = 'none';
                                  e.target.parentElement.innerHTML = '<div class="photo-placeholder-small"><i class="fas fa-user"></i></div>';
                                }}
                              />
                            ) : (
                              <div className="photo-placeholder-small"><i className="fas fa-user"></i></div>
                            )}
                          </div>
                          <div className="card-header-info">
                            <span className="card-name">{item.name || item.candidateName || 'N/A'}</span>
                            <div className="party-info-mobile">
                              <span className="party-badge-small">{item.partyShortName || 'IND'}</span>
                              {getSymbolUrl(item) && (
                                <img 
                                  src={getFullImageUrl(getSymbolUrl(item))} 
                                  alt="Symbol" 
                                  className="party-symbol-small"
                                  onError={(e) => e.target.style.display = 'none'}
                                />
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="card-body">
                          <div className="card-row">
                            <span className="card-label"><i className="fas fa-map-marked-alt"></i> Constituency:</span>
                            <span className="card-value">{item.constituencyCode} - {item.constituencyName || 'N/A'}</span>
                          </div>
                          <div className="card-row">
                            <span className="card-label"><i className="fas fa-birthday-cake"></i> Age:</span>
                            <span className="card-value">{item.age || 'N/A'}</span>
                          </div>
                          <div className="card-row">
                            <span className="card-label"><i className="fas fa-envelope"></i> Email:</span>
                            <span className="card-value">
                              {item.email ? <a href={`mailto:${item.email}`}>{item.email}</a> : '—'}
                            </span>
                          </div>
                          <div className="card-row">
                            <span className="card-label"><i className="fas fa-phone"></i> Mobile:</span>
                            <span className="card-value">
                              {item.mobile ? <a href={`tel:${item.mobile}`}>{item.mobile}</a> : '—'}
                            </span>
                          </div>
                          <div className="card-row">
                            <span className="card-label"><i className="fas fa-money-bill-wave"></i> Assets:</span>
                            <span className="card-value">{formatCurrency(item.assets)}</span>
                          </div>
                          <div className="card-row">
                            <span className="card-label"><i className="fas fa-balance-scale"></i> Criminal Cases:</span>
                            <span className="card-value">
                              <span className={`criminal-badge ${item.criminalCases > 0 ? 'has-cases' : 'no-cases'}`}>
                                {item.criminalCases || 0}
                              </span>
                            </span>
                          </div>
                          <div className="card-row">
                            <span className="card-label"><i className="fas fa-file-pdf"></i> Affidavit:</span>
                            <span className="card-value">
                              {item.affidavitPdfUrl ? (
                                <button
                                  className="btn-affidavit-mobile"
                                  onClick={() => openAffidavit(item.affidavitPdfUrl)}
                                  title="Download Affidavit PDF"
                                >
                                  <i className="fas fa-file-pdf"></i> View PDF
                                </button>
                              ) : (
                                '—'
                              )}
                            </span>
                          </div>
                          <div className="card-actions">
                            <button onClick={() => handleEdit(item)} className="btn-action btn-edit">
                              <i className="fas fa-edit"></i> Edit
                            </button>
                            <button onClick={() => handleDelete(item)} className="btn-action btn-delete">
                              <i className="fas fa-trash"></i> Delete
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
              </div>

              {totalPages > 1 && (
                <div className="pagination">
                  <button onClick={() => paginate(currentPage - 1)} disabled={currentPage === 1} className="btn-page">
                    <i className="fas fa-chevron-left"></i> Previous
                  </button>

                  <div className="page-numbers">
                    {[...Array(totalPages)].map((_, index) => {
                      const page = index + 1;
                      if (
                        page === 1 ||
                        page === totalPages ||
                        (page >= currentPage - 2 && page <= currentPage + 2)
                      ) {
                        return (
                          <button
                            key={page}
                            onClick={() => paginate(page)}
                            className={`btn-page ${page === currentPage ? 'active' : ''}`}
                          >
                            {page}
                          </button>
                        );
                      } else if (page === currentPage - 3 || page === currentPage + 3) {
                        return <span key={page} className="page-ellipsis">...</span>;
                      }
                      return null;
                    })}
                  </div>

                  <button
                    onClick={() => paginate(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className="btn-page"
                  >
                    Next <i className="fas fa-chevron-right"></i>
                  </button>
                </div>
              )}
            </>
          ) : (
            <div className="no-data">
              <i className="fas fa-inbox"></i>
              <p>{t('viewData.noData') || 'No data found'}</p>
            </div>
          )}
        </div>
      )}

      {showEditModal && selectedItem && (
        dataType === 'constituencies' ? (
          <EditConstituencyModal
            constituency={selectedItem}
            onClose={() => {
              setShowEditModal(false);
              setSelectedItem(null);
            }}
            onSuccess={handleUpdateSuccess}
          />
        ) : (
          <EditCandidateModal
            candidate={selectedItem}
            onClose={() => {
              setShowEditModal(false);
              setSelectedItem(null);
            }}
            onSuccess={handleUpdateSuccess}
          />
        )
      )}

      {deleteConfirm && (
        <div className="modal-overlay" onClick={() => setDeleteConfirm(null)}>
          <div className="modal-content delete-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>
                <i className="fas fa-exclamation-triangle"></i>
                Confirm Delete
              </h3>
              <button onClick={() => setDeleteConfirm(null)} className="close-button">×</button>
            </div>
            <div className="modal-body">
              <p>Are you sure you want to delete this {dataType === 'constituencies' ? 'constituency' : 'candidate'}?</p>
              <div className="delete-info">
                <strong>{deleteConfirm.name || deleteConfirm.candidateName}</strong>
                {dataType === 'candidates' && (
                  <small>{deleteConfirm.constituencyName} ({deleteConfirm.partyShortName})</small>
                )}
                {dataType === 'constituencies' && (
                  <small>{deleteConfirm.code} - {deleteConfirm.district}</small>
                )}
              </div>
              <p className="warning-text">
                <i className="fas fa-info-circle"></i>
                This action cannot be undone.
              </p>
            </div>
            <div className="modal-footer">
              <button onClick={() => setDeleteConfirm(null)} className="btn btn-secondary">Cancel</button>
              <button onClick={confirmDelete} className="btn btn-danger">
                <i className="fas fa-trash"></i> Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ViewData;
